import { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  User,
  Heart,
  Share2,
  ArrowLeft,
  Video,
  CheckCircle,
  UserCheck,
  Maximize2,
  X,
  Check,
  ChevronDown,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useParams, useLocation } from "react-router-dom";
import { fetchClassDetails } from "./fetchClasses";
import ClassBooking from "./ClassBooking";
import SEO from "../SEO";
import {
  getClassStatus,
  getCurrentLiveSession,
  getNextUpcomingSessionTime,
  getTimeUntilNextSession,
  isSessionLive,
  isSessionPassed,
  isClassBookable,
} from "../../utils/sessionTiming";
interface ClassData {
  _id: string;
  classTitle: string;
  classDescription: string;
  classStartDate: string;
  classEndDate: string;
  classDuration: string;
  classCategory: string;
  SerialCode: string;
  mentorId: string;
  classThumbnail: string;
  googleMeetLinks: Array<{
    url: string;
    time: string;
  }>;
  createAt: string;
  updateAt: string;
  classPrice: {
    session: string;
    monthly: string;
    yearly: string;
  };
  purchaseStatus: string;
  bookingId?: string; // Add this to handle booking ID from API
  pricingType?: string;
  demoDuration?: string;
  schedule?: {
    [day: string]: {
      isActive: boolean;
    };
  };
}

interface quickInfoInstructor {
  name: string;
  image: string;
  certification: string[] | string | undefined; // Updated to handle different types
  experience: string;
  languages?: string | string[];
}

interface BookingData {
  _id: string;
  classId: string;
  userId: string;
  status: "pending_payment" | "confirmed" | "cancelled" | "failed";
  isDemo: boolean;
  demoCompleted: boolean;
  bookingId: string;
}

interface UserDetails {
  name: string;
  email: string;
  age: string;
  phone: string;
}

interface classDetail {
  class: ClassData;
  instructor: quickInfoInstructor;
  userExists: boolean;
  booking: BookingData | null;
  demoTaken?: boolean;
  userDetails?: UserDetails;
}

interface ApiResponse {
  success: boolean;
  message?: string;
}

export default function ClassDetailsPage() {
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
  const [mentorData, setMentorData] = useState<quickInfoInstructor>();
  const [googleLinks, setGoogleLinks] = useState<string[]>([]);
  const [sessionTime, setSessionTime] = useState<string[]>([]);
  const [isUserExists, setIsUserExists] = useState<boolean>(false);
  const [demoTaken, setDemoTaken] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [classStatus, setClassStatus] = useState<{
    status: string;
    message: string;
  } | null>(null);

  // Updated booking state management
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [showBookingForm, setShowBookingForm] = useState<boolean>(false);
  const [showCertificateModal, setShowCertificateModal] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<string[]>([]);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState<boolean>(false);
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState<boolean>(false);
  const [isWhatYouNeedOpen, setIsWhatYouNeedOpen] = useState<boolean>(false);

  const handleShareClass = async () => {
    const shareTitle = `${classData?.classTitle || "Class"} | MetaFit Wellness`;
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        return;
      } catch {
        // dismissed
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
      toast.success("Class link copied to clipboard!");
    } catch {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
      toast.success("Class link copied!");
    }
  };

  const param = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vendorId = queryParams.get("vendorId");

  // Helper function to normalize certification data
  const normalizeCertifications = (
    cert: string[] | string | undefined
  ): string[] => {
    if (!cert) return [];
    if (Array.isArray(cert)) return cert;
    if (typeof cert === "string") return [cert];
    return [];
  };

  const getCertificateUrl = (cert: string): string => {
    const trimmed = (cert || "").trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    // Fallback for legacy/local uploads
    return `${window.location.origin}/uploads/${trimmed}`;
  };

  const isPdfUrl = (url: string): boolean => Boolean(url && typeof url === "string" && url.toLowerCase().includes(".pdf"));

  const handleFetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const rawClassId = param.userId || param.classId;

      if (!rawClassId) {
        throw new Error("Class ID not found");
      }

      let decodedClassId = rawClassId;
      try {
        const first = atob(rawClassId);
        try {
          const second = atob(first);
          decodedClassId = /^[0-9a-fA-F]{24}$/.test(second)
            ? second
            : (/^[0-9a-fA-F]{24}$/.test(first) ? first : second || rawClassId);
        } catch {
          decodedClassId = /^[0-9a-fA-F]{24}$/.test(first) ? first : rawClassId;
        }
      } catch {
        decodedClassId = rawClassId;
      }

      const response = (await fetchClassDetails(decodedClassId)) as classDetail;

      if (response && response.class) {
        const formatMeetTime = (value?: string) =>
          value
            ? value.replace(/undefined/gi, '').replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim()
            : 'TBD';

        setClassData(response.class);
        const meetLinks = Array.isArray(response.class.googleMeetLinks)
          ? response.class.googleMeetLinks
          : [];
        setGoogleLinks(meetLinks.map((link) => link.url));
        setSessionTime(meetLinks.map((times) => formatMeetTime(times.time)));

        // Set user existence
        setIsUserExists(response.userExists);

        // Set booking ID from backend response
        setBooking(response.booking || null);
        setDemoTaken(response.demoTaken || false);
        setUserDetails(response.userDetails || null);

        if (response.class.mentorId && response.instructor) {
          setMentorData({
            name: response.instructor.name || "Unknown Instructor",
            image: response.instructor.image,
            certification: response.instructor.certification, // Keep original data
            experience:
              response.instructor.experience || "No experience listed",
            languages: response.instructor.languages || "No languages listed",
          });
        }
      } else {
        throw new Error("No data received from API");
      }
    } catch (error) {
      console.error("Error fetching class data:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch class data"
      );
      setClassData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch class data from backend
  useEffect(() => {
    handleFetchDetail();
  }, [param.userId, param.classId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (classData) {
        const status = getClassStatus(classData, sessionTime);
        setClassStatus(status);
      }
    }, 5001); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [classData]);

  // Handle successful booking - update the backend booking ID
  const handleBookingSuccess = (newBookingId: string) => {
    handleFetchDetail();
    setShowBookingForm(false);
  };

  // Check if user has a valid booking (user exists AND backend has booking ID)
  const hasValidBooking =
    isUserExists &&
    booking &&
    booking.status === "confirmed" &&
    !booking.isDemo;
  const hasActiveDemo =
    isUserExists && booking && booking.isDemo && !booking.demoCompleted;

  // Removed localized functions: timeToMinutes, getNextUpcomingSessionTime, getCurrentLiveSession, getTimeUntilNextSession, getClassStatus, isSessionLive, isSessionPassed

  // Format date for display (DD:MM:YYYY)
  const formatDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };

  // Format date for display with day name
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDayOfWeek = (date: Date) => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[date.getDay()];
  };

  const handleBack = (): void => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-teal-50 to-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-500"></div>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-teal-50 to-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {error ? "Error Loading Class" : "Class Not Found"}
            </h2>
            <p className="text-gray-600 mb-4">
              {error || "The requested class could not be found."}
            </p>
            <button
              onClick={handleBack}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const liveSession = getCurrentLiveSession(sessionTime);
  const nextSession = getNextUpcomingSessionTime(classData, sessionTime);

  if (!classStatus) {
    const status = getClassStatus(classData, sessionTime);
    setClassStatus(status);
    return null;
  }

  const getPriceDisplayInfo = () => {
    const isPaid =
      classData?.classPrice &&
      (Number(classData.classPrice.session) > 0 ||
        Number(classData.classPrice.monthly) > 0 ||
        Number(classData.classPrice.yearly) > 0);

    if (classData?.pricingType === "demo" && isPaid) {
      return {
        priceText: `₹${classData.classPrice?.session || 0}`,
        type: "Premium Class",
        hasDemo: true,
      };
    }
    if (classData?.pricingType === "demo") {
      return { priceText: "Demo", type: "Demo Class", hasDemo: false };
    }
    if (classData?.purchaseStatus === "Free Tier" || !isPaid) {
      return { priceText: "Free", type: "Free Class", hasDemo: false };
    }
    return {
      priceText: `₹${classData?.classPrice?.session || 0}`,
      type: "Premium Class",
      hasDemo: false,
    };
  };

  const priceInfo = getPriceDisplayInfo();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased pb-24">
      <Toaster position="top-center" reverseOrder={false} />
      <SEO 
        title={classData.classTitle} 
        description={classData.classDescription} 
        image={classData.classThumbnail} 
        url={window.location.href} 
        keywords={`${classData.classTitle}, ${classData.classCategory}`}
      />

      {/* Main Content (Matching Luxury Wellness Retreats card stack) */}
      <div className="max-w-4xl mx-auto px-3 sm:px-5 space-y-2 pt-3 sm:pt-4">
        {/* 1. HERO POSTER / BANNER CARD */}
        <div className="w-full mt-0 mb-2 rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-2xs">
          <div
            onClick={() => setIsImageLightboxOpen(true)}
            className="relative w-full rounded-2xl overflow-hidden group cursor-pointer bg-slate-50 flex items-center justify-center"
          >
            {classData.classThumbnail ? (
              <img
                src={classData.classThumbnail}
                alt={classData.classTitle}
                className="w-full h-auto max-h-[360px] sm:max-h-[400px] object-contain block relative z-0 transition-transform duration-500 group-hover:scale-[1.01] mx-auto"
              />
            ) : (
              <div className="h-48 sm:h-64 w-full bg-gradient-to-r from-teal-500 to-emerald-600 flex items-center justify-center text-white text-base sm:text-lg font-bold">
                {classData.classTitle}
              </div>
            )}

            {/* Top Left Badges */}
            <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-10 flex-wrap">
              <span className="rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-md">
                {classData.classCategory}
              </span>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-md ${
                  classData.purchaseStatus === "Free Tier"
                    ? "bg-emerald-600/80"
                    : "bg-teal-600/80"
                }`}
              >
                {classData.purchaseStatus}
              </span>
              {hasValidBooking && (
                <span className="rounded-full bg-emerald-600/90 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-md">
                  ✓ Booked
                </span>
              )}
              {liveSession && hasValidBooking && (
                <span className="rounded-full bg-rose-600/90 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-md animate-pulse">
                  🔴 LIVE
                </span>
              )}
            </div>

            {/* Top Right Enlarge */}
            <div className="absolute top-2.5 right-2.5 opacity-90 group-hover:opacity-100 transition-opacity z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-sm">
                <Maximize2 className="h-3 w-3" />
                <span>Enlarge</span>
              </span>
            </div>
          </div>
        </div>

        {/* 2. TITLE & ORGANIZER / CLASS INFO CARD */}
        <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-slate-200 shadow-2xs">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h1 className="text-[14px] sm:text-[16px] font-bold text-slate-900 leading-tight">
                {classData.classTitle}
              </h1>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsBookmarked(!isBookmarked)}
                className={`flex items-center justify-center w-7 h-7 rounded-full transition-colors cursor-pointer shadow-2xs ${
                  isBookmarked
                    ? "text-rose-500 bg-rose-50"
                    : "text-slate-600 bg-slate-100 hover:bg-slate-200"
                }`}
                title={isBookmarked ? "Bookmarked" : "Bookmark"}
              >
                <Heart
                  className={`h-3.5 w-3.5 ${isBookmarked ? "fill-current" : ""}`}
                />
              </button>
              <button
                type="button"
                onClick={handleShareClass}
                className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 transition-all cursor-pointer shadow-2xs"
                title="Share Class"
              >
                {isCopied ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Share2 className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>
          <div className="pt-1.5 text-[11px] leading-tight font-normal space-y-1">
            {mentorData?.name && (
              <div className="flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
                <span className="text-slate-900 font-medium">{mentorData.name}</span>
                {mentorData.experience && (
                  <span className="text-slate-500">· {mentorData.experience}</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
              <span className="text-slate-900">
                Start: {formatDateOnly(classData.classStartDate)}
                {classData.classEndDate ? ` · End: ${formatDateOnly(classData.classEndDate)}` : " · Ongoing"}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
              <span className="text-slate-900">
                Duration: {classData.classDuration} · {sessionTime?.length || 0} Sessions/Day
                {nextSession ? ` · Next: ${nextSession.time}` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* 3. SCHEDULE & HIGHLIGHTS GRID */}
        <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-slate-200 shadow-2xs">
          <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-2">
            Class Schedule &amp; Details
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <span className="text-[10px] text-slate-500 font-medium">Start Date</span>
              <span className="font-semibold text-slate-900 mt-0.5 text-xs">{formatDateOnly(classData.classStartDate)}</span>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <span className="text-[10px] text-slate-500 font-medium">End Date</span>
              <span className="font-semibold text-slate-900 mt-0.5 text-xs">
                {classData.classEndDate ? formatDateOnly(classData.classEndDate) : "Ongoing"}
              </span>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <span className="text-[10px] text-slate-500 font-medium">Duration</span>
              <span className="font-semibold text-slate-900 mt-0.5 text-xs">{classData.classDuration}</span>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex flex-col justify-between">
              <span className="text-[10px] text-slate-500 font-medium">Daily Sessions</span>
              <span className="font-semibold text-slate-900 mt-0.5 text-xs">{sessionTime?.length || 0}</span>
            </div>
          </div>

          {classData.schedule && (
            <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2 text-xs">
              <span className="text-[11px] font-medium text-slate-600">Weekly Schedule:</span>
              <div className="flex flex-wrap gap-1">
                {Object.entries(classData.schedule)
                  .filter(([, details]) => details.isActive)
                  .map(([day]) => {
                    const isToday = getDayOfWeek(new Date()) === day.toLowerCase();
                    return (
                      <span
                        key={day}
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          isToday ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {day.substring(0, 3)}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Pricing Options Breakdown */}
          {(classData?.classPrice?.session || classData?.classPrice?.monthly || classData?.classPrice?.yearly) && (
            <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2 text-xs">
              <span className="text-[11px] font-medium text-slate-600">Pricing Options:</span>
              <div className="flex flex-wrap gap-2">
                {classData.classPrice?.session && (
                  <span className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-[10px] font-medium text-slate-800">
                    Session: <strong className="font-semibold text-slate-900">₹{classData.classPrice.session}</strong>
                  </span>
                )}
                {classData.classPrice?.monthly && (
                  <span className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-[10px] font-medium text-slate-800">
                    Monthly: <strong className="font-semibold text-slate-900">₹{classData.classPrice.monthly}</strong>
                  </span>
                )}
                {classData.classPrice?.yearly && (
                  <span className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-[10px] font-medium text-slate-800">
                    Yearly: <strong className="font-semibold text-slate-900">₹{classData.classPrice.yearly}</strong>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 4. SESSION STATUS & BOOKING CARD */}
        <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
              Session Status
            </h2>
            <span
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                classStatus.status === "live"
                  ? "bg-rose-100 text-rose-700 animate-pulse"
                  : classStatus.status === "upcoming"
                  ? "bg-teal-100 text-teal-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {classStatus.status === "live"
                ? "Live Now"
                : classStatus.status === "upcoming"
                ? "Upcoming"
                : "No Sessions"}
            </span>
          </div>

          {hasValidBooking && (
            <div className="p-2.5 mb-2 rounded-lg bg-emerald-50/80 border border-emerald-200 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-emerald-800 block text-[11px]">Booked Successfully</span>
                  <span className="text-emerald-700 font-mono text-[10px]">Booking ID: {booking?.bookingId}</span>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-700 font-medium">{classStatus.message}</p>
          {nextSession && (
            <p className="text-[11px] text-slate-500 mt-1">
              Next: <strong className="text-slate-800">{nextSession.time}</strong> ({getTimeUntilNextSession(classData, sessionTime)})
            </p>
          )}
        </div>

        {/* 5. ABOUT THIS CLASS */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 border border-slate-200 shadow-2xs">
          <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 mb-2">
            About This Class
          </h2>
          <div className="relative">
            <div
              className={`prose prose-slate max-w-none text-slate-700 leading-relaxed text-xs sm:text-sm text-[12px] break-words transition-all duration-300 ${
                !isSummaryExpanded ? "max-h-24 overflow-hidden" : ""
              }`}
            >
              <p className="whitespace-pre-line">{classData.classDescription}</p>
            </div>
            {!isSummaryExpanded && (classData.classDescription || "").length > 180 && (
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
            )}
          </div>
          {(classData.classDescription || "").length > 180 && (
            <div className="mt-2 flex justify-start">
              <button
                type="button"
                onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-900 hover:text-black transition-all cursor-pointer underline"
              >
                <span>{isSummaryExpanded ? "See Less" : "See More"}</span>
              </button>
            </div>
          )}
        </div>

        {/* 6. SESSION LINKS */}
        {classData.googleMeetLinks && classData.googleMeetLinks.length > 0 && (
          <div className="bg-white rounded-xl p-3 sm:p-3.5 border border-slate-200 shadow-2xs">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 mb-2.5">
              Session Links
            </h2>
            <div className="space-y-2">
              {(() => {
                if (!isUserExists) {
                  return (
                    <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <h4 className="font-medium text-slate-900 text-xs mb-1">
                        Login Required
                      </h4>
                      <p className="text-slate-500 text-[11px] mb-3">
                        Please log in to access session links and join live classes
                      </p>
                      <a
                        href="/login"
                        className="inline-block px-4 py-1.5 bg-black hover:bg-neutral-800 text-white text-xs font-semibold rounded-full transition-colors"
                      >
                        Login Now
                      </a>
                    </div>
                  );
                }

                const hasJoinableLinks =
                  classData.googleMeetLinks &&
                  classData.googleMeetLinks.some((link) => link.url);

                if ((hasValidBooking || hasActiveDemo) && hasJoinableLinks) {
                  return classData.googleMeetLinks.map((meetLink, index) => {
                    const timeStr = sessionTime[index] || meetLink.time;
                    const isLive = isSessionLive(timeStr, sessionTime);
                    const isPast = isSessionPassed(timeStr);
                    const isNext = nextSession?.time === timeStr;

                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2.5 rounded-lg border transition-all ${
                          isLive
                            ? "bg-rose-50/70 border-rose-200"
                            : isNext
                            ? "bg-emerald-50/70 border-emerald-200"
                            : isPast
                            ? "bg-slate-50/70 border-slate-100 opacity-60"
                            : "bg-white border-slate-200"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <Video
                            className={`h-4 w-4 ${
                              isLive
                                ? "text-rose-600"
                                : isNext
                                ? "text-emerald-600"
                                : isPast
                                ? "text-slate-400"
                                : "text-slate-700"
                            }`}
                          />
                          <div>
                            <span className="font-semibold text-slate-900 text-xs">
                              Slot {index + 1}
                              {isLive && (
                                <span className="ml-1.5 text-rose-600 text-[10px] font-bold">
                                  🔴 LIVE
                                </span>
                              )}
                              {isNext && !isLive && (
                                <span className="ml-1.5 text-emerald-600 text-[10px] font-bold">
                                  ⏰ NEXT
                                </span>
                              )}
                            </span>
                            <div className="text-[11px] text-slate-500">
                              {timeStr}
                              {isNext && (
                                <span className="ml-1 text-emerald-600 font-medium">
                                  ({getTimeUntilNextSession(classData, sessionTime)})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <a
                          href={meetLink.url || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: isPast ? '#64748b' : '#ffffff' }}
                          className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors shadow-xs inline-flex items-center justify-center ${
                            isLive
                              ? "bg-rose-600 hover:bg-rose-700 animate-pulse !text-white text-white"
                              : isNext
                              ? "bg-black hover:bg-neutral-800 !text-white text-white"
                              : isPast
                              ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                              : "bg-slate-900 hover:bg-black !text-white text-white"
                          }`}
                          onClick={isPast ? (e) => e.preventDefault() : undefined}
                        >
                          <span className={isPast ? "text-slate-500" : "!text-white text-white font-semibold"} style={{ color: isPast ? '#64748b' : '#ffffff' }}>
                            {isLive
                              ? "Join Live"
                              : isNext
                              ? "Join Next"
                              : isPast
                              ? "Ended"
                              : "Join Meeting"}
                          </span>
                        </a>
                      </div>
                    );
                  });
                }

                return (
                  <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <h4 className="font-medium text-slate-900 text-xs mb-1">
                      {isClassBookable(classData, sessionTime) ? "Booking Required" : "Class Ended"}
                    </h4>
                    <p className="text-slate-500 text-[11px] mb-3">
                      {isClassBookable(classData, sessionTime)
                        ? "Please book this class to access session links and join live classes"
                        : "This class has ended and is no longer accepting new bookings."}
                    </p>
                    {isClassBookable(classData, sessionTime) ? (
                      <button
                        onClick={() => setShowBookingForm(true)}
                        className="inline-block px-4 py-1.5 bg-black hover:bg-neutral-800 text-white text-xs font-semibold rounded-full transition-colors cursor-pointer"
                      >
                        Book Now
                      </button>
                    ) : (
                      <button
                        disabled
                        className="inline-block px-4 py-1.5 bg-slate-300 text-slate-600 text-xs font-semibold rounded-full cursor-not-allowed"
                      >
                        Class Expired
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* 7. MEET YOUR INSTRUCTOR */}
        {mentorData && (
          <div className="bg-white rounded-xl p-3 sm:p-3.5 border border-slate-200 shadow-2xs">
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 mb-2.5">
              Meet Your Instructor
            </h2>
            <div className="flex items-start gap-3">
              {mentorData.image ? (
                <img
                  src={mentorData.image}
                  alt={mentorData.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-800 text-sm font-bold flex-shrink-0">
                  {mentorData.name?.substring(0, 2).toUpperCase() || "IN"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 text-xs sm:text-sm">{mentorData.name}</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">{mentorData.experience || "Experienced Instructor"}</p>
                {mentorData.certification &&
                  normalizeCertifications(mentorData.certification).length > 0 && (
                    <div className="mt-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCertificate(
                            normalizeCertifications(mentorData.certification)
                              .map((c) => String(c).trim())
                              .filter(Boolean)
                          );
                          setShowCertificateModal(true);
                        }}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800 text-[10px] font-semibold rounded-md transition-colors inline-flex items-center gap-1 cursor-pointer"
                      >
                        View Certificates ({normalizeCertifications(mentorData.certification).length})
                      </button>
                    </div>
                  )}
                {mentorData.languages && (
                  <p className="text-slate-500 text-[11px] mt-1">
                    Languages: {Array.isArray(mentorData.languages) ? mentorData.languages.join(", ") : mentorData.languages}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 8. WHAT YOU'LL NEED (COLLAPSIBLE DROPDOWN / ACCORDION) */}
        <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
          <button
            type="button"
            onClick={() => setIsWhatYouNeedOpen(!isWhatYouNeedOpen)}
            className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                What You'll Need
              </h2>
            </div>
            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-200 ${
                  isWhatYouNeedOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>
          {isWhatYouNeedOpen && (
            <div className="px-3.5 pb-3.5 pt-2 border-t border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Check className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
                  <span className="text-[11px]">Stable internet connection</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Check className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
                  <span className="text-[11px]">
                    {(classData?.classCategory || "").includes("Yoga")
                      ? "Yoga mat & comfortable space"
                      : (classData?.classCategory || "").includes("Meditation")
                        ? "Quiet space for meditation"
                        : "Appropriate equipment and space"}
                  </span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Check className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
                  <span className="text-[11px]">Water bottle &amp; towel</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Check className="w-3.5 h-3.5 text-slate-900 flex-shrink-0" />
                  <span className="text-[11px]">Comfortable workout clothing</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 9. FIXED BOTTOM BOOKING BAR (MATCHING LUXURY WELLNESS RETREATS) */}
      {!showBookingForm && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-3 px-4 sm:px-8 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
            {/* Price Info */}
            <div className="flex flex-col min-w-0">
              <span className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none mb-0.5">
                {hasValidBooking ? "Your Booking" : "Starting from"}
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-base sm:text-[17px] font-bold text-slate-900 leading-tight">
                  {priceInfo.priceText}
                </span>
                <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">
                  / {priceInfo.type}
                </span>
              </div>
              {priceInfo.hasDemo && !hasValidBooking && (
                <span className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                  Demo available for this class
                </span>
              )}
            </div>

            {/* Action Button */}
            <div className="flex items-center flex-shrink-0">
              {hasValidBooking ? (
                liveSession ? (
                  <a
                    href={googleLinks[liveSession.index]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#ffffff' }}
                    className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 !text-white text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 animate-pulse inline-flex items-center justify-center cursor-pointer"
                  >
                    <span className="!text-white text-white font-semibold" style={{ color: '#ffffff' }}>🔴 Join Live</span>
                  </a>
                ) : nextSession ? (
                  <a
                    href={googleLinks[nextSession.index]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#ffffff' }}
                    className="px-6 py-2.5 bg-black hover:bg-neutral-800 !text-white text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 inline-flex items-center justify-center cursor-pointer"
                  >
                    <span className="!text-white text-white font-semibold" style={{ color: '#ffffff' }}>⏰ Join Next ({nextSession.time})</span>
                  </a>
                ) : (
                  <span className="px-5 py-2 bg-emerald-100 text-emerald-800 rounded-full font-semibold text-xs">
                    ✓ Booked
                  </span>
                )
              ) : isUserExists && booking && booking.isDemo ? (
                isClassBookable(classData, sessionTime) ? (
                  <button
                    type="button"
                    onClick={() => setShowBookingForm(true)}
                    style={{ color: '#ffffff' }}
                    className="px-6 py-2.5 bg-black hover:bg-neutral-800 !text-white text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 cursor-pointer inline-flex items-center justify-center"
                  >
                    <span className="!text-white text-white font-semibold" style={{ color: '#ffffff' }}>Purchase Now</span>
                  </button>
                ) : (
                  <button
                    disabled
                    className="px-6 py-2.5 bg-slate-300 text-slate-600 rounded-full font-semibold text-[13px] cursor-not-allowed"
                  >
                    Class Expired
                  </button>
                )
              ) : isClassBookable(classData, sessionTime) ? (
                <button
                  type="button"
                  onClick={() => setShowBookingForm(true)}
                  style={{ color: '#ffffff' }}
                  className="px-6 py-2.5 bg-black hover:bg-neutral-800 !text-white text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 cursor-pointer inline-flex items-center justify-center"
                >
                  <span className="!text-white text-white font-semibold" style={{ color: '#ffffff' }}>Book Now</span>
                </button>
              ) : (
                <button
                  disabled
                  className="px-6 py-2.5 bg-slate-300 text-slate-600 rounded-full font-semibold text-[13px] cursor-not-allowed"
                >
                  Class Expired
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 10. IMAGE LIGHTBOX MODAL */}
      {isImageLightboxOpen && classData.classThumbnail && (
        <div
          onClick={() => setIsImageLightboxOpen(false)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 cursor-pointer"
        >
          <button
            type="button"
            onClick={() => setIsImageLightboxOpen(false)}
            className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-4xl overflow-hidden rounded-2xl bg-white p-2 shadow-2xl"
          >
            <img
              src={classData.classThumbnail}
              alt={classData.classTitle}
              className="max-h-[85vh] w-auto object-contain mx-auto rounded-xl"
            />
          </div>
        </div>
      )}


      {/* ClassBooking Modal/Component - Fixed z-index */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <ClassBooking
              classData={classData}
              onClose={() => setShowBookingForm(false)}
              onBookingSuccess={handleBookingSuccess}
              ClassId={param.userId || param.classId || ""}
              userDetails={userDetails}
              demoCompleted={booking?.demoCompleted || false}
              vendorId={vendorId}
            />
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      {showCertificateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Instructor Certifications</h2>
                <button
                  onClick={() => setShowCertificateModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-6 rounded-xl border border-teal-200">
                <div className="space-y-3">
                  {selectedCertificate.map((cert: string, index: number) => {
                    const url = getCertificateUrl(cert);
                    const showPreview = Boolean(url);
                    const isPdf = url ? isPdfUrl(url) : false;

                    return (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border-l-4 border-teal-600"
                      >
                        <div className="text-2xl text-teal-600 flex-shrink-0">✓</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-gray-800 font-semibold">
                                Certificate {index + 1}
                              </p>
                              <p className="text-gray-600 text-sm break-all">
                                {cert.trim()}
                              </p>
                            </div>
                            {url && (
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-md transition-colors flex-shrink-0"
                              >
                                Open
                              </a>
                            )}
                          </div>

                          {showPreview && (
                            <div className="mt-3">
                              {isPdf ? (
                                <iframe
                                  src={url}
                                  className="w-full h-64 rounded-lg border border-gray-200 bg-white"
                                  title={`Certificate PDF ${index + 1}`}
                                />
                              ) : (
                                <img
                                  src={url}
                                  alt={`Certificate ${index + 1}`}
                                  className="w-full max-h-64 object-contain rounded-lg border border-gray-200 bg-white"
                                  onError={(e) => {
                                    // Hide broken images instead of showing a giant broken icon
                                    e.currentTarget.style.display = "none";
                                  }}
                                  loading="lazy"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowCertificateModal(false)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
